import { smsTestingAPI } from './sms-testing.api'
import type {
  SmsComposeBody,
  SmsListParams,
  SmsMessage,
  SmsWaitForBody
} from './sms-testing.types'

export function useSmsTestingService() {
  const list = async (params: SmsListParams = {}): Promise<SmsMessage[]> => {
    const response = await smsTestingAPI.list(params)
    return response.data.messages
  }

  /**
   * Paginated list. Returns `{ messages, nextCursor }`; pass back
   * `nextCursor` as `cursor` on the next call to walk pages. Null cursor
   * = terminal page.
   */
  const listPage = async (
    params: SmsListParams = {}
  ): Promise<{ messages: SmsMessage[]; nextCursor: string | null }> => {
    const response = await smsTestingAPI.list(params)
    return { messages: response.data.messages, nextCursor: response.data.nextCursor }
  }

  const get = async (id: string): Promise<SmsMessage> => {
    const response = await smsTestingAPI.get(id)
    return response.data.message
  }

  const clear = async (to: string): Promise<number> => {
    const response = await smsTestingAPI.clear(to)
    return response.data.deleted
  }

  const compose = async (body: SmsComposeBody): Promise<SmsMessage> => {
    const response = await smsTestingAPI.compose(body)
    // The /sms response is Twilio-shaped; map it back to SmsMessage shape.
    return {
      id: response.data.sid.replace(/^SM/, ''),
      messageSid: response.data.sid,
      to: response.data.to,
      from: response.data.from,
      body: response.data.body,
      mediaUrls: body.mediaUrls ?? [],
      status: 'queued',
      providerMetadata: body.providerMetadata ?? {},
      // ADX compose path is operator-driven — there's no merchant
      // context, mirroring the sink's inject route which also tags
      // composed messages with `merchantId: null`.
      merchantId: null,
      rawSize: 0,
      receivedAt: response.data.date_created,
      expiresAt: response.data.date_created
    }
  }

  const waitFor = async (body: SmsWaitForBody): Promise<SmsMessage | null> => {
    const response = await smsTestingAPI.waitFor(body)
    return response.data.message
  }

  return { list, listPage, get, clear, compose, waitFor }
}
